import { useState, useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import { GET_CAMPAIGN_AI_SUGGESTIONS } from '@/graphql/media-buying-queries'
import { showNotification } from '@/utils/notifications'

/** Removes leading number counters (e.g. "1. ", "2) ") and trailing dots from AI suggestions */
const normalizeSuggestion = (text) => {
    if (typeof text !== 'string') return text
    return text
        .replace(/^\d+[\.\)]\s*/, '') // Remove "1. ", "2) ", etc.
        .replace(/\.$/, '') // Remove trailing dot
        .trim()
}

export const useAISuggestions = (campaignId) => {
    const [aiSuggestions, setAiSuggestions] = useState({
        headers: [],
        subheaders: [],
        descriptions: [],
    })

    const [fetchAISuggestions, { loading, error }] = useLazyQuery(GET_CAMPAIGN_AI_SUGGESTIONS, {
        onCompleted: (data) => {
            const campaign = data.campaigns?.[0]
            if (campaign) {
                const parsedHeaders = campaign.headers ? JSON.parse(campaign.headers) : []
                const parsedSubheaders = campaign.subheaders ? JSON.parse(campaign.subheaders) : []
                const parsedDescriptions = campaign.descriptions ? JSON.parse(campaign.descriptions) : []

                // The newest seven nightly groups, so a license older than a week still gets fresh copy
                const extractAndNormalize = (parsed) =>
                    parsed
                        .slice(-7)
                        .flatMap((entry) => entry.array || [])
                        .map((s) => normalizeSuggestion(s))
                        .filter(Boolean)

                setAiSuggestions({
                    headers: extractAndNormalize(parsedHeaders),
                    subheaders: extractAndNormalize(parsedSubheaders),
                    descriptions: extractAndNormalize(parsedDescriptions),
                })
            }
        },
        onError: (err) => {
            console.error('AI Suggestions failed:', err)
            showNotification('Failed to load AI suggestions', 'error')
        }
    })

    // Fetch suggestions when campaign ID is available
    useEffect(() => {
        if (campaignId) {
            fetchAISuggestions({ variables: { campaignId } })
        }
    }, [campaignId])

    return {
        aiSuggestions,
        loading,
        error,
        refetch: () => fetchAISuggestions({ variables: { campaignId } })
    }
}